import {
	Box,
	Checkbox,
	Collapse,
	FormControl,
	InputLabel,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	ListSubheader,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import { Job } from "../../../api";
import { JobsPriority, getJobPriorities } from "../../../utilities/types";
import { useJobSelectionViewModel } from "./JobSelectionList.vm";
import "./JobSelectionList.css";
import { Notes } from "@mui/icons-material";
import PortalTooltip from "../../helpers/PortalTooltip";
import PriorityIcon from "./PriorityIcon";

interface JobSelectionListProps {
	jobs: Job[];
	value: Job[];
	onChange: (jobs: Job[]) => void;
	error?: string;
}

export function JobSelectionList(props: JobSelectionListProps) {
	const { jobs, value, onChange, error } = props;

	// Use ViewModel hook to get all business logic and data
	const vm = useJobSelectionViewModel(jobs, value, onChange);

	return (
		<Box className={`job-selection-list${error ? " error" : ""}`}>
			<List>
				{/* Select All - Global */}
				<ListItem disablePadding disableGutters className="select-all">
					<ListItemButton onClick={vm.handleSelectAll} dense>
						<Checkbox
							edge="start"
							checked={vm.isAllSelected}
							indeterminate={vm.isPartiallySelected}
							tabIndex={-1}
							disableRipple
						/>
						<ListItemText primary="Select All" />
					</ListItemButton>
				</ListItem>

				{vm.sortedGrowerNames.map((growerName) => (
					<Box key={growerName} className="grower-group">
						<ListSubheader component="div" onClick={() => vm.handleSelectAllForGrower(growerName)}>
							<Checkbox
								checked={vm.isGrowerFullySelected(growerName)}
								indeterminate={vm.isGrowerPartiallySelected(growerName)}
								edge="start"
								onClick={(e) => {
									e.stopPropagation();
									vm.handleSelectAllForGrower(growerName);
								}}
							/>
							<Typography>{growerName}</Typography>
						</ListSubheader>
						{vm.groupedJobs[growerName].map((job) => (
							<Box key={job.id} className={vm.isJobChecked(job) ? "job-wrapper checked" : "job-wrapper"}>
								<ListItem disablePadding className="job-item">
									<ListItemButton
										role={undefined}
										onClick={() => vm.handleToggle(job)}
										dense
										className={vm.isJobChecked(job) ? "checked" : undefined}
										aria-expanded={vm.isJobExpanded(job)}
									>
										<Checkbox
											edge="start"
											checked={vm.isJobChecked(job)}
											tabIndex={-1}
											disableRipple
											inputProps={{ "aria-labelledby": `checkbox-list-label-${job.id}` }}
										/>
										<ListItemText id={`checkbox-list-label-${job.id}`} primary={vm.getJobLabel(job)} />
										{vm.isJobChecked(job) && (
											<>
												<PriorityIcon
													color={vm.getPriorityIconColor(job)}
													tooltip={vm.getPriorityTooltip(job)}
													onClick={(e) => {
														e.stopPropagation();
														vm.handlePriorityIconClick(job);
													}}
												/>
												<PortalTooltip
													title={vm.hasSubmissionNotes(job) ? "View/edit submission notes" : "Add submission notes"}
												>
													<Notes
														id="notes-icon"
														className="icon"
														sx={{ color: vm.hasSubmissionNotes(job) ? "warning.main" : "inherit" }}
														onClick={(e) => {
															e.stopPropagation();
															vm.handleNotesIconClick(job);
														}}
													/>
												</PortalTooltip>
											</>
										)}
									</ListItemButton>
								</ListItem>
								<Collapse in={vm.isJobExpanded(job)} timeout="auto" unmountOnExit>
									<Box className="job-expanded-content">
										<Box className="job-expanded-fields">
											{vm.isNotesVisible(job) && (
												<TextField
													label="Submission Notes"
													value={vm.getSelectedJob(job)?.submissionNotes || ""}
													onChange={(e) => vm.handleNotesChange(job, e.target.value)}
													multiline
													fullWidth
													size="small"
													variant="outlined"
												/>
											)}
											{vm.isPriorityVisible(job) && (
												<FormControl fullWidth size="small">
													<InputLabel id={`priority-label-${job.id}`}>Priority</InputLabel>
													<Select
														labelId={`priority-label-${job.id}`}
														id={`priority-select-${job.id}`}
														value={vm.getSelectedJob(job)?.fieldPriority ?? job.fieldPriority}
														label="Field Priority"
														onChange={(e) => vm.handlePriorityChange(job, e.target.value as JobsPriority)}
													>
														{getJobPriorities().map((priority) => (
															<MenuItem key={priority} value={priority}>
																{priority}
															</MenuItem>
														))}
													</Select>
												</FormControl>
											)}
										</Box>
									</Box>
								</Collapse>
							</Box>
						))}
					</Box>
				))}
			</List>
			{error && (
				<Typography variant="caption" color="error" className="error-text">
					{error}
				</Typography>
			)}
		</Box>
	);
}
